import React from 'react';

import { isNil, omit } from 'ramda';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getNightsCount } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/ChronologicalPickers/utils';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { EOrganizedBy, ETripEvent, ITripEvent } from 'detailsv2/structure';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { isDefined } from 'utils/ramda';

import { useAccomodationWizardContext } from '../../../context';
import { usePropertyPageQuery } from '../../../hooks';
import { TFormValues } from '../../types';

export const useAccomodationFormContext = () => {
  const {
    updateData,
    goToStep,
    submitData,
    tripEvent,
    setIsSuggestedArea,
    data: { default: accommodation },
  } = useAccomodationWizardContext();
  const methods = useFormContext<TFormValues>();

  const { formState, handleSubmit, watch } = methods;

  const attachedPage = watch('attachedPage');

  const isVisibleFormBody = !!attachedPage;
  const isEditing = isDefined(tripEvent?.attachedPages);

  const propertyQueryRes = usePropertyPageQuery({ pageId: attachedPage?.id });
  const isAccomodationAlertVisible = React.useMemo(() => {
    if (!attachedPage?.id) return true;
    if (isNil(propertyQueryRes.data)) return false;
    return !propertyQueryRes.isFetching && !propertyQueryRes.isValidPropertyPage;
  }, [propertyQueryRes.isFetching, propertyQueryRes.isValidPropertyPage]);

  const isInvalid = !formState.isValid;
  const isDisabled = isInvalid;

  const isOverideWarning = React.useMemo(() => {
    if (isNil(tripEvent.id)) return false;
    if (!tripEvent.showChangedRoomOrPageTooltip) return false;
    return attachedPage?.id !== tripEvent?.attachedPages?.[0]?.id;
  }, [tripEvent, attachedPage]);

  const updateWizardData = handleSubmit((values: TFormValues) => {
    try {
      if (!values.attachedPage) throw new Error('Attached page is required');

      const formValues = omit(['attachedPage'], values);
      const accomodationValues = {
        ...accommodation,
        attachedPages: [{ id: values.attachedPage.id as string, name: values.attachedPage.name }],
        ...formValues,
        data: {
          ...formValues.data,
          rooming: values.data.rooming,
        },
      } as Partial<ITripEvent<ETripEvent.Accommodation>>; // TODO: refactor type system, the form asks for data that is not needed, because of which it is necessary to explicitly convert it to the following type

      updateData('default', accomodationValues);
    } catch {
      showErrorSnackbar(GENERAL_ERROR_MESSAGE);
    }
  });

  const submitForm = async () => {
    await updateWizardData();
    submitData();
  };
  const goToSuggestedArea = async () => {
    setIsSuggestedArea(true);

    await updateWizardData();
    goToStep('suggested-area');
  };

  const goToOwnedArea = async () => {
    setIsSuggestedArea(false);

    await updateWizardData();
    goToStep('suggested-area');
  };

  const organizedBy = useSelector<unknown, EOrganizedBy>(itineraryScheduleTypeSelector);

  const range = watch(organizedBy === 'days' ? 'daysRange' : 'datesRange');
  const nightsCount = getNightsCount(range.start!, range.end || undefined); // TODO types mistmatch
  const isDayRoom = nightsCount == 0;

  return {
    methods,

    submitForm,
    goToSuggestedArea,
    goToOwnedArea,

    isVisibleFormBody,
    isDisabled,
    isOverideWarning,
    isEditing,
    isAccomodationAlertVisible,

    isDayRoom,
    nightsCount,
  };
};
